// App.js - Complete with all shop-prefixed routes

import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getStoredUser, isSuperAdmin, canAccessPath, getHomePath } from './utils/permissions';
import { installSessionGuards, isSessionExpired, clearSession } from './utils/session';
import { CurrencyProvider } from './context/CurrencyContext.js';

import Sidebar from './components/Sidebar';
import Settings from './pages/Settings/Settings.js';

// Pages
import CurrencySettingTabs from './pages/Currencysettings/CurrencysettingTabs';
import Invoicedetails from './pages/Invoicedetail/Invoicedetails';
import PlaceOrders from './pages/Placeorders/Placeorders';
import Orders from './pages/Orders/Orders.js';
import InvoiceDetail from './pages/Orders/InvoiceDetail'; // ✅ Added InvoiceDetail Import
import Statements from './pages/Statements/Statements.js';
import Postings from './pages/Postings/Postings.js';
import InactiveCustomers from './pages/inActiveCustomers/InactiveCustomers.js';
import StaffMembers from './pages/Staff/StaffMembers.js';
import Brands from './pages/brand/Brands.js';
import Types from './pages/type/Types.js';
import Variations from './pages/variation/Variations.js';
import Questions from './pages/Question/Questions.js';
import Payments from './pages/Payment/Payments.js';
import Manufacturingdata from './pages/data/Manufacturingdata.js';
import AddManufacturingdata from './pages/data/AddManufacturingdata.js';
import AddnewStaffpage from './pages/Staff/AddnewStaffpage.js';
import Categories from './pages/Categories/Categories.js';
import ProductList from './pages/ProductList/ProductList.js';
import Reviews from './pages/Reviews/Reviews.js';
import Messages from './pages/Messages/Messages.js';
import Chats from './pages/Chats/Chats.js'; // ✅ ADDED: Chats Import
import Expenses from './pages/Expenses/Expenses.js';
import PurchaseOrders from './pages/PurchaseOrders';
import LotTracking from './pages/LotTracking/LotTracking.js';
import Logout from './pages/Logout';
import Implantcompanys from './pages/Implantcompany/Implantcompanys.js';
import Dashboard from './pages/Dashboard/Dashboard';
import Carts from './pages/cart/Carts';
import './App.css';
import SignIn from './pages/Auth/Signin';

import Navbar from './components/Navbar.js';
import useImplantMode from './utils/useImplantMode';
import Products from './pages/Product/Products.js';
import Customerspayment from './pages/Customer/Customerspayment.js';
import AddCustomerspayment from './pages/Customer/AddCustomerspayment.js';
import Imagesgallery from './pages/Images/Imagesgallery.js';
import Customers from './pages/ActiveCustomers/Customers.js';
import MyProfile from './pages/Profile/Profile.js';
import SelectUser from './pages/Users/users.js';
import AddUser from './pages/Users/AddUser.js';
import CompanyAddresses from './pages/CompanyAddresses/CompanyAddresses.js';
import Bills from './pages/bills/Bills.js';
import Suppliers from './pages/supplier/Suppliers.js';
import Shop from './pages/Shop/Shop.js';
import Notes from './pages/Note/Notes.js';
import Issuestock from './pages/stock/Issuestock.js';
import AddIssuestock from './pages/stock/AddIssuestock.js';
import OrderstatusImage from './pages/Image/OrderstatusImage.js';
import Addresss from './pages/Addresss/Addresss.js';
import AddShop from './pages/Shop/AddShop.js';
import EditShop from './pages/Shop/EditShop.js';
import ShopDetails from './pages/Shop/ShopDetails.js';
import OrderDetail from './pages/Orders/OrderDetail.js';
import Ordernotes from './pages/Ordernote/Ordernotes.js';
import LotTrackingDetail from './pages/LotTracking/LotTrackingdetail.js';
import StatementsDetail from './pages/Statements/StatementsDetail.js';
import Ordersproduct from './pages/orderproduct/Ordersproduct.js';
import EditUser from './pages/Users/EditUser.js';
import EditCustomer from './pages/ActiveCustomers/EditCustomer';
import EditStaff from './pages/Staff/EditStaff';
import StockMaterial from './pages/StockMaterial/StockMaterial.js';
import AddStockMaterial from './pages/StockMaterial/AddStockMaterial.js';

import EditProductPage from './pages/ProductList/EditProductPage.js';

// Logout Handler
const handleLogout = () => {
  localStorage.clear();
  sessionStorage.clear();
  window.location.href = '/signin';
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const location = useLocation();

  const isAuthenticated =
    localStorage.getItem('isAuthenticated') === 'true' &&
    !!localStorage.getItem('access_token');
  const user = getStoredUser();

  // Session (access token) expired -> clear login data and go to sign in
  if (isAuthenticated && isSessionExpired()) {
    clearSession();
    return <Navigate to="/signin" replace />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/signin" replace />;
  }

  // SUPER_ADMIN: all shops, all pages
  if (isSuperAdmin(user)) {
    return children;
  }

  // STAFF must have a shop
  if (!user.shop_id) {
    return <Navigate to="/signin" replace />;
  }

  const ownShopId = String(user.shop_id);

  // Always keep STAFF inside their own shop
  localStorage.setItem('selectedShopId', ownShopId);
  localStorage.setItem('selected_shop_id', ownShopId);
  if (user.shop_name) {
    localStorage.setItem('selectedShopName', user.shop_name);
  }

  const shopMatch = location.pathname.match(/^\/shop\/(\d+)/);
  if (!shopMatch || shopMatch[1] !== ownShopId) {
    const rest = location.pathname.replace(/^\/shop\/\d+/, '');
    const target = `/shop/${ownShopId}${rest === '/' ? '' : rest}`;
    return <Navigate to={`${target}${location.search}`} replace />;
  }

  // Page permission check (shop management is SUPER_ADMIN only)
  if (!canAccessPath(user, location.pathname)) {
    return <Navigate to={getHomePath(user)} replace />;
  }

  return children;
};

// Main Layout
const MainLayout = ({ children }) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  // Implant button: changing the mode re-mounts only the page area, so the
  // page fetches its data again - Navbar/Sidebar and the app itself stay.
  const implantMode = useImplantMode();
  return (
    <>
      {isAuthenticated && <Navbar onLogout={handleLogout} />}
      {isAuthenticated && <Sidebar />}
      <div className={`main-content ${isAuthenticated ? 'with-sidebar' : 'full-width'}`}>
        <div className="content-wrapper" key={implantMode ? 'implant' : 'all'}>
          {children}
        </div>
      </div>
    </>
  );
};

// Page Wrapper
const PageWrapper = ({ children }) => (
  <div className="page-wrapper">
    <div className="page-content">
      {children}
    </div>
  </div>
);

// Main App
const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated') === 'true';
    setIsAuthenticated(authStatus);
  }, []);

  // Attaches the login token to every fetch()/axios call and signs out when the session ends
  useEffect(() => {
    installSessionGuards();
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
  };

  return (
    <CurrencyProvider>
      <Routes>

        {/* ====================== AUTH ROUTES ====================== */}
        <Route path="/signin" element={<SignIn onLogin={handleLogin} />} />


        {/* ====================== SHOP MANAGEMENT ROUTES ====================== */}
        <Route
          path="/shops"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Shop />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ✅ NEW: Shop prefixed route */}
        <Route
          path="/shop/:shopId/shops"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Shop />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/add-shop"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AddShop />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ✅ NEW: Shop prefixed route */}
        <Route
          path="/shop/:shopId/add-shop"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AddShop />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/edit-shop/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EditShop />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ✅ NEW: Shop prefixed route */}
        <Route
          path="/shop/:shopId/edit-shop/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EditShop />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/shop-details/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ShopDetails />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ✅ NEW: Shop prefixed route */}
        <Route
          path="/shop/:shopId/shop-details/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ShopDetails />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== DASHBOARD ====================== */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Dashboard />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/shop/:shopId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Dashboard />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== ORDERS ====================== */}
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Orders />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/orders"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Orders />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== ✅ INVOICE DETAIL ====================== */}
        <Route
          path="/invoice-detail/:trackingId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <InvoiceDetail />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/invoice-detail/:trackingId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <InvoiceDetail />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== STATEMENTS ====================== */}
        <Route
          path="/statements"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Statements />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/statements"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Statements />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== POSTINGS ====================== */}
        <Route
          path="/postings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Postings />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/postings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Postings />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== CUSTOMERS ====================== */}
        <Route
          path="/customers"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Customers />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/customers"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Customers />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

 <Route
          path="/edit-customer/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EditCustomer />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/edit-customer/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EditCustomer />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* ====================== INACTIVE CUSTOMERS ====================== */}
        <Route
          path="/inactive-customers"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <InactiveCustomers />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/inactive-customers"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <InactiveCustomers />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== STAFF ====================== */}
        <Route
          path="/staff"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <StaffMembers />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/staff"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <StaffMembers />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/addnewstaff"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <AddnewStaffpage />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/addnewstaff"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <AddnewStaffpage />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

          {/* ====================== EDIT STAFF ====================== */}
<Route
  path="/edit-staff/:id"
  element={
    <ProtectedRoute>
      <MainLayout>
        <EditStaff />
      </MainLayout>
    </ProtectedRoute>
  }
/>
<Route
  path="/shop/:shopId/edit-staff/:id"
  element={
    <ProtectedRoute>
      <MainLayout>
        <EditStaff />
      </MainLayout>
    </ProtectedRoute>
  }
/>

        {/* ====================== CATEGORIES ====================== */}
        <Route
          path="/categories"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Categories />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/categories"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Categories />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== PRODUCTS ====================== */}
        <Route
          path="/products"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Products />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/products"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Products />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/productlist"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <ProductList />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/productlist"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <ProductList />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ✅ NEW: PRODUCT EDIT PAGE */}
        <Route
          path="/productlist/edit/:productId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <EditProductPage />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/productlist/edit/:productId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <EditProductPage />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== REVIEWS ====================== */}
        <Route
          path="/reviews"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Reviews />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/reviews"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Reviews />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== MESSAGES ====================== */}
        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Messages />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/messages"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Messages />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== ✅ Chats ====================== */}
        <Route
          path="/Chats"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Chats />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Chats"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Chats />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== EXPENSES ====================== */}
        <Route
          path="/expenses"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Expenses />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/expenses"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Expenses />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== BILLS ====================== */}
        <Route
          path="/bills"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Bills />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/bills"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Bills />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== SUPPLIERS ====================== */}
        <Route
          path="/suppliers"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Suppliers />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/suppliers"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Suppliers />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== PURCHASE ORDERS ====================== */}
        <Route
          path="/purchase-orders"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <PurchaseOrders />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/purchase-orders"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <PurchaseOrders />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== LOT TRACKING ====================== */}
        <Route
          path="/Lot-tracking"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <LotTracking />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Lot-tracking"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <LotTracking />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/Lot-trackingdetail/:trackingId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <LotTrackingDetail />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/lot-trackingdetail/:trackingId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <LotTrackingDetail />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/order-detail/:trackingId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <OrderDetail />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/order-detail/:trackingId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <OrderDetail />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== PROFILE ====================== */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <MyProfile />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/profile"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <MyProfile />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== SETTINGS ====================== */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Settings />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Settings />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== USERS ====================== */}
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <SelectUser />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/users"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <SelectUser />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-user"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AddUser />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/add-user"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AddUser />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-user/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EditUser />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/edit-user/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EditUser />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== COMPANY ADDRESSES ====================== */}
        <Route
          path="/company-addresses"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <CompanyAddresses />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/company-addresses"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <CompanyAddresses />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== CURRENCY SETTINGS ====================== */}
        <Route
          path="/currency-settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <CurrencySettingTabs />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/currency-settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <CurrencySettingTabs />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== INVOICE DETAILS ====================== */}
        <Route
          path="/invoice-details"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Invoicedetails />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/invoice-details"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Invoicedetails />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== STATEMENT DETAILS ====================== */}
        <Route
          path="/statement-details/:invoiceNumber"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <StatementsDetail />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/statement-details/:invoiceNumber"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <StatementsDetail />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== PLACE ORDERS ====================== */}
        <Route
          path="/place-orders"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <PlaceOrders />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/place-orders"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <PlaceOrders />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== IMPLANT COMPANIES ====================== */}
        <Route
          path="/Implant-companys"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Implantcompanys />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Implant-companys"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Implantcompanys />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== CARTS ====================== */}
        <Route
          path="/Carts"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Carts />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Carts"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Carts />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== ADDRESSS ====================== */}
        <Route
          path="/Addresss"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Addresss />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Addresss"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Addresss />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== BRANDS ====================== */}
        <Route
          path="/Brands"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Brands />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Brands"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Brands />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== TYPES ====================== */}
        <Route
          path="/Types"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Types />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Types"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Types />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== VARIATIONS ====================== */}
        <Route
          path="/Variations"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Variations />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Variations"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Variations />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== MANUFACTURING DATA ====================== */}
        <Route
          path="/Manufacturingdata"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Manufacturingdata />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Manufacturingdata"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Manufacturingdata />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/AddManufacturingdata"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <AddManufacturingdata />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/AddManufacturingdata"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <AddManufacturingdata />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== QUESTIONS ====================== */}
        <Route
          path="/Questions"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Questions />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Questions"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Questions />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== PAYMENTS ====================== */}
        <Route
          path="/Payments"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Payments />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Payments"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Payments />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== IMAGES GALLERY ====================== */}
        <Route
          path="/Imagesgallery"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Imagesgallery />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Imagesgallery"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Imagesgallery />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== CUSTOMERS PAYMENT ====================== */}
        <Route
          path="/Customerspayment"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Customerspayment />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Customerspayment"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Customerspayment />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== ADD CUSTOMERS PAYMENT ====================== */}
        <Route
          path="/Add-Customers-payment"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <AddCustomerspayment />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Add-Customers-payment"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <AddCustomerspayment />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== ORDERS PRODUCT ====================== */}
        <Route
          path="/Orders-product"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Ordersproduct />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Orders-product"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Ordersproduct />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== NOTES ====================== */}
        <Route
          path="/Notes"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Notes />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Notes"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Notes />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== ISSUE STOCK ====================== */}
        <Route
          path="/Issuestock"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Issuestock />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Issuestock"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Issuestock />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/AddIssuestock"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <AddIssuestock />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/AddIssuestock"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <AddIssuestock />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* ====================== ✅ STOCK MATERIAL - LIST ====================== */}

        <Route
          path="/StockMaterial"
          element={

            <ProtectedRoute>

              <MainLayout>

                <PageWrapper>

                  <StockMaterial />

                </PageWrapper>

              </MainLayout>

            </ProtectedRoute>

          }

        />

        <Route

          path="/shop/:shopId/StockMaterial"

          element={

            <ProtectedRoute>

              <MainLayout>

                <PageWrapper>

                  <StockMaterial />

                </PageWrapper>

              </MainLayout>

            </ProtectedRoute>

          }

        />



        {/* ====================== ✅ STOCK MATERIAL - ADD/EDIT ====================== */}

        <Route

          path="/AddStockMaterial"

          element={

            <ProtectedRoute>

              <MainLayout>

                <PageWrapper>

                  <AddStockMaterial />

                </PageWrapper>

              </MainLayout>

            </ProtectedRoute>

          }

        />

        <Route

          path="/shop/:shopId/AddStockMaterial"

          element={

            <ProtectedRoute>

              <MainLayout>

                <PageWrapper>

                  <AddStockMaterial />

                </PageWrapper>

              </MainLayout>

            </ProtectedRoute>

          }

        />
        {/* ====================== ORDER STATUS IMAGE ====================== */}
        <Route
          path="/OrderstatusImage"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <OrderstatusImage />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/OrderstatusImage"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <OrderstatusImage />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== ORDER NOTES ====================== */}
        <Route
          path="/Ordernotes"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Ordernotes />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:shopId/Ordernotes"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PageWrapper>
                  <Ordernotes />
                </PageWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ====================== LOGOUT ====================== */}
        <Route
          path="/shop/:shopId/logout"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Logout onLogout={handleLogout} />
              </MainLayout>
            </ProtectedRoute>
          }
        />

      </Routes>
    </CurrencyProvider>
  );
};

export default App;